/**
 * Unlock Progression Service
 *
 * Evaluates company state against location unlock requirements.
 * Determines which locations should be unlocked based on game progress.
 */

import { CampusLocation, UnlockRequirement } from '../../types/spatial';
import { getUnlockRequirements, hasUnlockRequirements } from '../../config/locationConfig';
import type { CompanyState } from '../../types/company';
import type { Product } from '@smartphone-tycoon/shared-core';

export interface UnlockCheckResult {
  /** Whether all requirements are met */
  met: boolean;
  /** List of requirements that are not yet met */
  unmetRequirements: UnlockRequirement[];
  /** List of requirements that are met */
  metRequirements: UnlockRequirement[];
  /** Progress percentage (0-100) */
  progressPercent: number;
}

export interface UnlockProgressionState {
  cash: number;
  reputation: number;
  employeeCount: number;
  productsInDevelopment: number;
  productsReleased: number;
  researchCompleted: number;
}

/**
 * Development statuses that count as "in development"
 */
const IN_DEVELOPMENT_STATUSES = [
  'concept',
  'development',
  'design',
  'engineering',
  'testing',
  'manufacturing',
  'production',
] as const;

/**
 * Extract relevant state for unlock checking
 */
export function extractUnlockState(
  companyState: CompanyState,
  products: Product[]
): UnlockProgressionState {
  const safeProducts = products ?? [];
  return {
    cash: companyState.cash || 0,
    reputation: companyState.reputation || 0,
    employeeCount: companyState.employees?.length || 0,
    productsInDevelopment: safeProducts.filter(
      (p) => IN_DEVELOPMENT_STATUSES.includes(p.status as typeof IN_DEVELOPMENT_STATUSES[number])
    ).length,
    productsReleased: safeProducts.filter(
      (p) => p.status === 'released'
    ).length,
    researchCompleted: 0, // TODO: Integrate with research system
  };
}

/**
 * Check if a single requirement is met
 */
function checkRequirement(
  requirement: UnlockRequirement,
  state: UnlockProgressionState
): { met: boolean; currentValue: number } {
  switch (requirement.type) {
    case 'cash':
      return {
        met: state.cash >= requirement.value,
        currentValue: state.cash,
      };

    case 'reputation':
      return {
        met: state.reputation >= requirement.value,
        currentValue: state.reputation,
      };

    case 'employees':
      return {
        met: state.employeeCount >= requirement.value,
        currentValue: state.employeeCount,
      };

    case 'products': {
      // "products" requirement counts both in-development and released
      const totalProducts = state.productsInDevelopment + state.productsReleased;
      return {
        met: totalProducts >= requirement.value,
        currentValue: totalProducts,
      };
    }

    case 'productsInDevelopment':
      return {
        met: state.productsInDevelopment >= requirement.value,
        currentValue: state.productsInDevelopment,
      };

    case 'productsReleased':
      return {
        met: state.productsReleased >= requirement.value,
        currentValue: state.productsReleased,
      };

    case 'research':
      return {
        met: state.researchCompleted >= requirement.value,
        currentValue: state.researchCompleted,
      };

    default:
      // Unknown requirement type - treat as not met
      return { met: false, currentValue: 0 };
  }
}

/**
 * Check all requirements for a location
 */
export function checkLocationRequirements(
  location: CampusLocation,
  state: UnlockProgressionState
): UnlockCheckResult {
  // If no requirements, it's automatically unlocked
  if (!hasUnlockRequirements(location)) {
    return {
      met: true,
      unmetRequirements: [],
      metRequirements: [],
      progressPercent: 100,
    };
  }

  const requirements = getUnlockRequirements(location);
  const metRequirements: UnlockRequirement[] = [];
  const unmetRequirements: UnlockRequirement[] = [];

  for (const req of requirements) {
    const { met } = checkRequirement(req, state);
    if (met) {
      metRequirements.push(req);
    } else {
      unmetRequirements.push(req);
    }
  }

  const progressPercent =
    requirements.length > 0
      ? Math.round((metRequirements.length / requirements.length) * 100)
      : 100;

  return {
    met: unmetRequirements.length === 0,
    unmetRequirements,
    metRequirements,
    progressPercent,
  };
}

/**
 * Check all locations and return which ones should be newly unlocked
 */
export function checkAllLocationUnlocks(
  state: UnlockProgressionState,
  currentlyUnlocked: CampusLocation[]
): {
  newlyUnlocked: CampusLocation[];
  allResults: Map<CampusLocation, UnlockCheckResult>;
} {
  const allLocations = Object.values(CampusLocation);
  const newlyUnlocked: CampusLocation[] = [];
  const allResults = new Map<CampusLocation, UnlockCheckResult>();

  for (const location of allLocations) {
    // Skip already unlocked locations
    if (currentlyUnlocked.includes(location)) {
      continue;
    }

    const result = checkLocationRequirements(location, state);
    allResults.set(location, result);

    if (result.met) {
      newlyUnlocked.push(location);
    }
  }

  return { newlyUnlocked, allResults };
}

/**
 * Unlock Progression Service
 *
 * Provides methods for checking and managing location unlock progression.
 */
export const unlockProgressionService = {
  /**
   * Check requirements for a specific location
   */
  checkRequirements(
    location: CampusLocation,
    companyState: CompanyState,
    products: Product[]
  ): UnlockCheckResult {
    const state = extractUnlockState(companyState, products);
    return checkLocationRequirements(location, state);
  },

  /**
   * Check all locations for new unlocks
   */
  checkAllUnlocks(
    companyState: CompanyState,
    products: Product[],
    currentlyUnlocked: CampusLocation[]
  ): CampusLocation[] {
    const state = extractUnlockState(companyState, products);
    const { newlyUnlocked } = checkAllLocationUnlocks(state, currentlyUnlocked);
    return newlyUnlocked;
  },

  /**
   * Get progress for a specific location
   */
  getProgress(
    location: CampusLocation,
    companyState: CompanyState,
    products: Product[]
  ): {
    progressPercent: number;
    requirements: UnlockRequirement[];
    unmetRequirements: UnlockRequirement[];
  } {
    const state = extractUnlockState(companyState, products);
    const result = checkLocationRequirements(location, state);
    const requirements = getUnlockRequirements(location);

    return {
      progressPercent: result.progressPercent,
      requirements,
      unmetRequirements: result.unmetRequirements,
    };
  },
};

export default unlockProgressionService;
